import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, X, Send, Users, Sparkles, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Premium color palette
const colors = {
  gold: '#D4AF37',
  goldLight: '#F5E6B8',
  black: '#030303',
  white: '#FFFFFF'
};

// Animated Section Component
const Section = ({ children, className = "", dark = false }) => (
  <section 
    className={`
      min-h-screen flex flex-col items-center justify-center 
      px-6 py-20 md:py-24 relative overflow-hidden
      ${dark ? 'bg-[#020202]' : 'bg-[#050505]'}
      ${className}
    `}
  >
    {children}
  </section>
);

// Reveal Animation
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
  >
    {children}
  </motion.div>
);

// Main QR Experience Component
export default function QRExperience() {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll();
  
  // Ritual intro state
  const [ritualStep, setRitualStep] = useState(0); // 0: intro, 1: touched, 2: moment, 3: full experience
  const [crystalTouched, setCrystalTouched] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  
  // Interactive ritual state
  const [userWord, setUserWord] = useState('');
  const [userName, setUserName] = useState('');
  const [communityMessages, setCommunityMessages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedWord, setSubmittedWord] = useState('');
  
  useEffect(() => {
    setIsLoaded(true);
    fetchCommunityMessages();
    fetchMessageCount();
  }, []);

  // Handle crystal touch
  const handleCrystalTouch = () => {
    if (crystalTouched) return;
    
    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
    
    setCrystalTouched(true);
    
    // Show thank you message after animation
    setTimeout(() => {
      setShowThankYou(true);
    }, 1500);
  };

  // Continue to moment screen
  const continueToMoment = () => {
    setRitualStep(2);
  };

  // Continue to full experience
  const continueToExperience = () => {
    setRitualStep(3);
  };

  const fetchCommunityMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/water/messages/random?limit=12`);
      if (response.ok) {
        const data = await response.json();
        setCommunityMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchMessageCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/water/messages/count`);
      if (response.ok) {
        const data = await response.json();
        setTotalCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const handleSubmitWord = async (e) => {
    e.preventDefault();
    if (!userWord.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/water/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          word: userWord.trim(),
          name: userName.trim() || null
        })
      });
      
      if (response.ok) {
        setSubmittedWord(userWord.trim());
        setHasSubmitted(true);
        setTotalCount(prev => prev + 1);
        // Refresh community messages
        setTimeout(fetchCommunityMessages, 1000);
      }
    } catch (error) {
      console.error('Error submitting word:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 6 concepts in the crystal
  const crystalConcepts = [
    "Agua",
    "Gratitud", 
    "Naturaleza",
    "Equilibrio",
    "Territorio",
    "Conexión"
  ];

  // 9 words of the ritual
  const ritualWords = [
    "Amor", "Gratitud", "Agua", 
    "Naturaleza", "Territorio", "Encuentro",
    "Historia", "Equilibrio", "Armonía"
  ];

  return (
    <div 
      className="font-sans text-white overflow-x-hidden"
      style={{ 
        fontFamily: '"Inter", system-ui, sans-serif',
        background: colors.black 
      }}
    >
      {/* Ambient Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.02, 0.05, 0.02]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-[-30%] left-[-30%] w-[100%] h-[100%] rounded-full"
          style={{ 
            background: `radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 60%)`,
            filter: 'blur(100px)' 
          }}
        />
      </div>

      <AnimatePresence mode="wait">
        {/* ===== RITUAL SCREEN 1: TOUCH THE CRYSTAL ===== */}
        {ritualStep === 0 && (
          <motion.div
            key="ritual-intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <div className="text-center max-w-md mx-auto">
              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl md:text-5xl mb-12"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                Love & Thanks
                <br />
                <span className="italic opacity-80">Water</span>
              </motion.h1>

              {/* Instruction */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mb-12"
              >
                <p className="text-lg sm:text-xl opacity-70 font-light mb-2">
                  Antes de beber…
                </p>
                <p className="text-xl sm:text-2xl font-light" style={{ color: colors.gold }}>
                  toca el cristal.
                </p>
              </motion.div>

              {/* Interactive Crystal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="relative mb-12"
              >
                <motion.button
                  onClick={handleCrystalTouch}
                  disabled={crystalTouched}
                  whileHover={!crystalTouched ? { scale: 1.05 } : {}}
                  whileTap={!crystalTouched ? { scale: 0.95 } : {}}
                  className="relative focus:outline-none"
                  aria-label="Tocar el cristal"
                >
                  {/* Glow effect */}
                  <motion.div
                    animate={crystalTouched ? {
                      scale: [1, 1.5, 1.2],
                      opacity: [0.3, 0.8, 0.4]
                    } : {
                      scale: [1, 1.1, 1],
                      opacity: [0.2, 0.4, 0.2]
                    }}
                    transition={{ 
                      duration: crystalTouched ? 1.5 : 3, 
                      repeat: crystalTouched ? 0 : Infinity 
                    }}
                    className="absolute inset-0 rounded-full"
                    style={{ 
                      background: `radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)`,
                      filter: 'blur(30px)',
                      transform: 'scale(1.5)'
                    }}
                  />
                  
                  {/* Crystal image */}
                  <motion.img
                    src="/landing-assets/cristal.png"
                    alt="Crystal Symbol"
                    className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 relative z-10"
                    animate={crystalTouched ? {
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.1, 1.05]
                    } : {}}
                    transition={{ duration: 1.5 }}
                    style={{ 
                      filter: crystalTouched 
                        ? 'drop-shadow(0 0 60px rgba(212,175,55,0.8))' 
                        : 'drop-shadow(0 0 30px rgba(212,175,55,0.4))'
                    }}
                  />
                </motion.button>

                {/* Touch hint */}
                {!crystalTouched && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.5 }}
                    transition={{ delay: 2 }}
                    className="mt-6 text-xs uppercase tracking-[0.3em]"
                  >
                    Tocar el símbolo
                  </motion.p>
                )}
              </motion.div>

              {/* Thank you message after touch */}
              <AnimatePresence>
                {showThankYou && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="space-y-6"
                  >
                    <p 
                      className="text-3xl sm:text-4xl"
                      style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
                    >
                      Gracias.
                    </p>
                    
                    <div className="space-y-3 mt-8">
                      <p className="text-base sm:text-lg opacity-70 font-light">
                        Ahora piensa en una palabra.
                      </p>
                      <p className="text-lg sm:text-xl opacity-90 font-light">
                        Una palabra que represente este momento.
                      </p>
                    </div>

                    {/* Continue button */}
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 }}
                      onClick={continueToMoment}
                      className="mt-10 flex flex-col items-center gap-2 mx-auto group"
                    >
                      <span className="text-sm uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 transition-opacity">
                        Continuar
                      </span>
                      <motion.div
                        animate={{ y: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ChevronDown className="w-5 h-5 opacity-60" />
                      </motion.div>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* ===== RITUAL SCREEN 2: THE MOMENT ===== */}
        {ritualStep === 2 && (
          <motion.div
            key="ritual-moment"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <div className="text-center max-w-md mx-auto">
              {/* Title */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xs sm:text-sm uppercase tracking-[0.4em] mb-8 font-medium"
                style={{ color: colors.gold }}
              >
                El momento
              </motion.p>

              {/* Crystal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="mb-12"
              >
                <motion.img
                  src="/landing-assets/cristal.png"
                  alt="Crystal"
                  className="w-32 h-32 sm:w-40 sm:h-40 mx-auto"
                  animate={{ 
                    rotate: [0, 1, -1, 0],
                    y: [0, -5, 0]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  style={{ filter: 'drop-shadow(0 0 40px rgba(212,175,55,0.5))' }}
                />
              </motion.div>

              {/* Instructions */}
              <div className="space-y-8">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="text-xl sm:text-2xl font-light"
                  style={{ fontFamily: '"Cinzel", serif' }}
                >
                  Mira el puente de Besalú.
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="text-xl sm:text-2xl font-light"
                  style={{ fontFamily: '"Cinzel", serif' }}
                >
                  Respira.
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 3 }}
                  className="text-xl sm:text-2xl font-light"
                  style={{ fontFamily: '"Cinzel", serif' }}
                >
                  Piensa en tu palabra.
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 4.5 }}
                  className="text-2xl sm:text-3xl font-light pt-4"
                  style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
                >
                  Ahora bebe.
                </motion.p>
              </div>

              {/* Continue to full experience */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 6 }}
                onClick={continueToExperience}
                className="mt-16 flex flex-col items-center gap-2 mx-auto group"
              >
                <span className="text-sm uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 transition-opacity">
                  Descubrir más
                </span>
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ChevronDown className="w-5 h-5 opacity-60" />
                </motion.div>
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ===== FULL EXPERIENCE (after ritual) ===== */}
        {ritualStep === 3 && (
          <motion.div
            key="full-experience"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >

      {/* ===== HERO - INTRO ===== */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          {/* Crystal Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="mb-6"
          >
            <img 
              src="/landing-assets/cristal.png" 
              alt="Crystal Symbol"
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 mx-auto"
              style={{ filter: 'drop-shadow(0 0 50px rgba(212,175,55,0.4))' }}
            />
          </motion.div>

          {/* Besalú '26 */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-2xl sm:text-3xl md:text-4xl mb-8"
            style={{ fontFamily: '"Cinzel", serif', fontWeight: 300, color: colors.gold }}
          >
            Besalú '26
          </motion.h2>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-10"
            style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
          >
            Love & Thanks
            <br />
            <span className="italic opacity-80">Water</span>
          </motion.h1>

          {/* Hook Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="space-y-4"
          >
            <p className="text-lg sm:text-xl md:text-2xl font-light opacity-90">
              El agua está en todas partes.
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-light opacity-50">
              Pero rara vez es memorable.
            </p>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 2 }}
            className="mt-16"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ChevronDown className="w-6 h-6 mx-auto" />
            </motion.div>
          </motion.div>
        </div>
      </Section>

      {/* ===== ORIGIN STORY ===== */}
      <Section dark>
        <div className="text-center max-w-2xl mx-auto px-4">
          <Reveal>
            <p 
              className="text-base sm:text-lg md:text-xl leading-relaxed font-light"
              style={{ lineHeight: '2' }}
            >
              Este símbolo nace de la{' '}
              <span className="opacity-100 font-normal">belleza oculta en los cristales del agua</span>
              <br />
              y del{' '}
              <span style={{ color: colors.gold }}>puente medieval de Besalú</span>,
              <br />
              un vínculo atemporal entre naturaleza, historia y territorio.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* ===== SECTION 3 - LA INTENCIÓN ===== */}
      <Section>
        <div className="text-center max-w-2xl mx-auto px-4">
          <Reveal>
            <h2 
              className="text-4xl sm:text-5xl md:text-6xl mb-10"
              style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
            >
              La intención
            </h2>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="text-base sm:text-lg md:text-xl font-light opacity-80 mb-6 leading-relaxed">
              El investigador japonés <span className="text-white font-normal">Masaru Emoto</span> exploró una idea fascinante.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <p className="text-base sm:text-lg md:text-xl font-light opacity-70 mb-10 leading-relaxed">
              Que el agua puede reflejar la vibración de nuestras palabras.
            </p>
          </Reveal>

          {/* Three Words */}
          <Reveal delay={0.4}>
            <div className="flex justify-center items-center gap-6 md:gap-10 mb-10">
              {["Amor", "Gratitud", "Paz"].map((word, i) => (
                <span 
                  key={word}
                  className="text-2xl sm:text-3xl md:text-4xl"
                  style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
                >
                  {word}.
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.5}>
            <p className="text-base sm:text-lg md:text-xl font-light opacity-60 mb-12 leading-relaxed">
              Palabras que, según su investigación, podían cambiar la forma de los cristales del agua.
            </p>
          </Reveal>

          {/* Quote Banner */}
          <Reveal delay={0.6}>
            <div 
              className="py-8 px-6 md:px-10 rounded-2xl mb-12"
              style={{ 
                background: 'rgba(212,175,55,0.05)',
                border: '1px solid rgba(212,175,55,0.15)'
              }}
            >
              <p 
                className="text-lg sm:text-xl md:text-2xl italic leading-relaxed opacity-90"
                style={{ fontFamily: '"Cinzel", serif' }}
              >
                "El poder infinito del agua ha sido cristalizado para crear un mundo de verdad y armonía."
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.7}>
            <div className="space-y-4">
              <p className="text-base sm:text-lg md:text-xl font-light opacity-80">
                Esta botella nace de esa inspiración.
              </p>
              <p 
                className="text-lg sm:text-xl md:text-2xl font-light"
                style={{ color: colors.gold }}
              >
                Una invitación a beber con intención.
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ===== SECTION 6 - EL SÍMBOLO ===== */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto px-4">
          <Reveal>
            <h2 
              className="text-4xl sm:text-5xl md:text-6xl mb-8"
              style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
            >
              El símbolo
            </h2>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="text-base sm:text-lg md:text-xl font-light opacity-70 mb-12">
              El cristal representa la armonía invisible del agua.
              <br />
              En su geometría viven <span style={{ color: colors.gold }}>seis ideas</span>:
            </p>
          </Reveal>

          {/* 6 Concepts Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-14">
            {crystalConcepts.map((concept, i) => (
              <Reveal key={concept} delay={0.1 * i}>
                <motion.div
                  whileHover={{ scale: 1.05, borderColor: 'rgba(212,175,55,0.5)' }}
                  className="py-5 px-4 rounded-xl transition-all duration-300"
                  style={{ 
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}
                >
                  <span 
                    className="text-xl sm:text-2xl md:text-3xl"
                    style={{ fontFamily: '"Cinzel", serif' }}
                  >
                    {concept}
                  </span>
                </motion.div>
              </Reveal>
            ))}
          </div>

          {/* Bridge Explanation */}
          <Reveal delay={0.6}>
            <div className="space-y-4 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-base sm:text-lg md:text-xl font-light opacity-80">
                En el centro aparece el <span style={{ color: colors.gold }}>puente de Besalú</span>.
              </p>
              <p className="text-base sm:text-lg opacity-50 font-light italic">
                Un símbolo de paso entre pasado y presente.
              </p>
            </div>
          </Reveal>

          {/* Crystal Image */}
          <Reveal delay={0.7}>
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 1, 0]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="mt-12"
            >
              <img 
                src="/landing-assets/cristal.png" 
                alt="Crystal with Bridge"
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 mx-auto opacity-80"
                style={{ filter: 'drop-shadow(0 0 40px rgba(212,175,55,0.3))' }}
              />
            </motion.div>
          </Reveal>
        </div>
      </Section>

      {/* ===== SECTION 9 - EL RITUAL ===== */}
      <Section>
        <div className="text-center max-w-3xl mx-auto px-4">
          <Reveal>
            <h2 
              className="text-4xl sm:text-5xl md:text-6xl mb-12"
              style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
            >
              Nueve palabras que resumen el ritual
            </h2>
          </Reveal>

          {/* 9 Words Grid */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-xl mx-auto">
            {ritualWords.map((word, i) => (
              <Reveal key={word} delay={0.05 * i}>
                <motion.div
                  whileHover={{ scale: 1.08 }}
                  className="py-4 px-3 rounded-lg"
                  style={{ 
                    background: i === 0 || i === 8 ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${i === 0 || i === 8 ? 'rgba(212,175,55,0.25)' : 'rgba(255,255,255,0.06)'}`
                  }}
                >
                  <span 
                    className="text-base sm:text-lg md:text-xl font-light"
                    style={{ 
                      fontFamily: '"Cinzel", serif',
                      color: i === 0 || i === 8 ? colors.gold : 'white'
                    }}
                  >
                    {word}
                  </span>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== INTERACTIVE RITUAL - PARTICIPA ===== */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto px-4 w-full">
          <Reveal>
            <div className="mb-8">
              <Sparkles className="w-8 h-8 mx-auto mb-4" style={{ color: colors.gold, opacity: 0.7 }} />
              <h2 
                className="text-3xl sm:text-4xl md:text-5xl"
                style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
              >
                Deja tu palabra para el agua de Besalú
              </h2>
            </div>
          </Reveal>

          {/* Input Form or Success Message */}
          <Reveal delay={0.2}>
            <AnimatePresence mode="wait">
              {!hasSubmitted ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  onSubmit={handleSubmitWord}
                  className="max-w-md mx-auto space-y-4 mb-12"
                >
                  <div className="relative">
                    <input
                      type="text"
                      value={userWord}
                      onChange={(e) => setUserWord(e.target.value)}
                      placeholder="Tu palabra de gratitud..."
                      maxLength={50}
                      className="w-full px-6 py-4 rounded-full text-center text-base sm:text-lg font-light bg-transparent outline-none transition-all duration-300 focus:border-[#D4AF37]"
                      style={{ 
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: 'white'
                      }}
                    />
                  </div>
                  
                  <div className="relative">
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Tu nombre (opcional)"
                      maxLength={30}
                      className="w-full px-6 py-3 rounded-full text-center text-sm font-light bg-transparent outline-none transition-all duration-300 focus:border-[#D4AF37]/50"
                      style={{ 
                        border: '1px solid rgba(255,255,255,0.08)',
                        color: 'white'
                      }}
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={!userWord.trim() || isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-40"
                    style={{ 
                      background: colors.gold,
                      color: colors.black
                    }}
                  >
                    {isSubmitting ? (
                      <span>Enviando...</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Dejar mi palabra</span>
                      </>
                    )}
                  </motion.button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="max-w-md mx-auto mb-12 py-8 px-6 rounded-2xl"
                  style={{ 
                    background: 'rgba(212,175,55,0.08)',
                    border: '1px solid rgba(212,175,55,0.2)'
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(212,175,55,0.2)' }}
                  >
                    <Check className="w-8 h-8" style={{ color: colors.gold }} />
                  </motion.div>
                  <p className="text-lg sm:text-xl mb-2 font-light">Tu palabra ha sido añadida</p>
                  <p 
                    className="text-2xl sm:text-3xl mb-4"
                    style={{ fontFamily: '"Cinzel", serif', color: colors.gold }}
                  >
                    "{submittedWord}"
                  </p>
                  <p className="text-sm opacity-50">
                    Ahora forma parte del ritual colectivo
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>

          {/* Community Counter */}
          <Reveal delay={0.3}>
            <div className="flex items-center justify-center gap-3 mb-10">
              <Users className="w-5 h-5 opacity-50" />
              <p className="text-sm opacity-60">
                <span style={{ color: colors.gold }} className="font-semibold">{totalCount}</span> personas han participado en el ritual
              </p>
            </div>
          </Reveal>

          {/* Community Messages Wall */}
          {communityMessages.length > 0 && (
            <Reveal delay={0.4}>
              <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <p className="text-xs uppercase tracking-[0.3em] mb-6 opacity-40">
                  Palabras de la comunidad
                </p>
                <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                  {communityMessages.map((msg, i) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.03 * i }}
                      whileHover={{ scale: 1.05, borderColor: 'rgba(212,175,55,0.4)' }}
                      className="px-4 py-2 rounded-full text-sm transition-all duration-300"
                      style={{ 
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}
                    >
                      <span className="font-light">{msg.word}</span>
                      {msg.name && (
                        <span className="ml-2 text-xs opacity-40">— {msg.name}</span>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </Section>

      {/* ===== CLOSING ===== */}
      <Section dark>
        <div className="text-center max-w-2xl mx-auto px-4">
          {/* Crystal */}
          <Reveal>
            <motion.div
              animate={{ 
                scale: [1, 1.02, 1],
                opacity: [0.7, 0.9, 0.7]
              }}
              transition={{ duration: 5, repeat: Infinity }}
              className="mb-12"
            >
              <img 
                src="/landing-assets/cristal.png" 
                alt="Crystal"
                className="w-24 h-24 sm:w-28 sm:h-28 mx-auto"
                style={{ filter: 'drop-shadow(0 0 30px rgba(212,175,55,0.4))' }}
              />
            </motion.div>
          </Reveal>

          <Reveal delay={0.2}>
            <h2 
              className="text-4xl sm:text-5xl md:text-6xl mb-6"
              style={{ fontFamily: '"Cinzel", serif', fontWeight: 300 }}
            >
              Love & Thanks
              <br />
              <span className="italic">Water</span>
            </h2>
          </Reveal>

          <Reveal delay={0.4}>
            <p 
              className="text-base sm:text-lg md:text-xl font-light opacity-60 mb-12"
            >
              Un pequeño ritual nacido en <span style={{ color: colors.gold }}>Besalú</span>.
            </p>
          </Reveal>

          {/* Return to Pont10 */}
          <Reveal delay={0.6}>
            <motion.a
              href="/landing"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-all duration-300"
              style={{ 
                border: '1px solid rgba(212,175,55,0.4)',
                color: colors.gold
              }}
            >
              Descubrir Pont10
            </motion.a>
          </Reveal>

          {/* Footer */}
          <Reveal delay={0.8}>
            <div className="mt-20 pt-10" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <img 
                src="/landing-assets/pont10-logo.png" 
                alt="Pont10"
                className="h-10 sm:h-12 mx-auto opacity-60"
              />
              <p className="mt-6 text-[10px] sm:text-xs text-white/30 uppercase tracking-[0.2em]">
                Besalú · 2026
              </p>
            </div>
          </Reveal>
        </div>
      </Section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
        
        html {
          scroll-behavior: smooth;
        }
        
        ::-webkit-scrollbar {
          width: 4px;
        }
        
        ::-webkit-scrollbar-track {
          background: #030303;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #222;
          border-radius: 10px;
        }
        
        ::selection {
          background: rgba(212, 175, 55, 0.3);
          color: white;
        }
      `}</style>
    </div>
  );
}
